import { useEffect, useState } from 'react';
import { actionType } from 'config/constants';
import { Box, CircularProgress, FormControlLabel, Grid, IconButton, TextField, Typography } from '@material-ui/core';
import { isNil } from 'lodash';
import { PHONE_REGEX } from 'config/constants';
import { useFormik } from 'formik';
import { useTheme } from '@material-ui/core/styles';
import * as Yup from 'yup';
import ActionOnEmployeeModal from 'components/ActionOnEmployee';
import CheckIcon from '@material-ui/icons/Check';
import ControlPointIcon from '@material-ui/icons/ControlPoint';
import DefaultButton from 'components/DefaultButton';
import DefaultMenuItem from 'components/DefaultMenuItem';
import DefaultSwitch from 'components/DefaultSwitch';
import moment from 'moment';
import SwitchStatus from 'components/SwitchStatus';
import useShowActionModal from 'hooks/useShowActionModal';
import useStyles from './styles';

const validationSchema = Yup.object({
  birthdate: Yup.string().required('Champ obligatoire'),
  city: Yup.string().required('Champ obligatoire'),
  country: Yup.string().required('Champ obligatoire'),
  email: Yup.string().email('Veuillez saisir une adresse mail valide').required('Champ obligatoire'),
  first_name: Yup.string().required('Champ obligatoire'),
  gender: Yup.string().required('Champ obligatoire'),
  last_name: Yup.string().required('Champ obligatoire'),
  level: Yup.string().required('Champ obligatoire'),
  phone: Yup.string()
    .matches(PHONE_REGEX, 'Veuillez saisir un numéro de téléphone valide')
    .required('Champ obligatoire'),
  role: Yup.string().required('Champ obligatoire'),
  service_disabling: Yup.string().nullable(),
});

const placeholder =
  'https://external-content.duckduckgo.com/iu/?u=http://www.pixelstalk.net/wp-content/uploads/2016/10/Blank-Wallpaper-HD.jpg&f=1&nofb=1';

function EmployeeForm({
  autoSavePicture = false,
  activities,
  dialog,
  disabled,
  employee,
  id,
  onCancel = () => {},
  onPictureChange = () => {},
  onStatusChange = () => {},
  onSubmit = () => {},
}) {
  const classes = useStyles();
  const theme = useTheme();

  const [enabled, setEnabled] = useState(Boolean(employee.is_enabled));
  const [actionLoading, setActionLoading] = useState(false);

  const { errors, handleChange, handleSubmit, isSubmitting, resetForm, setValues, touched, values } = useFormik({
    initialValues: {
      ...employee,
      birthdate: moment(employee.birthdate).format('YYYY-MM-DD'),
      city: employee?.city ?? '',
      country: employee?.country ?? 'France',
      gender: employee?.gender ?? '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (autoSavePicture) await onSubmit({ values, picture: null });
      else {
        const { pictureSizeError, pictureUpdated } = await onSubmit({ values, picture });
        if (!pictureSizeError)
          setPicture({ file: null, url: pictureUpdated ? picture.url : employee?.profilePictureURL ?? null });
      }
    },
  });

  const [picture, setPicture] = useState({ file: null, url: employee?.profilePictureURL ?? null });
  const [isUpdatingPicture, setIsUpdatingPicture] = useState(false);

  const {
    previousChoice,
    resetShowAction,
    setActionConflictSelect,
    setActionSelect,
    setActionTransfer,
    shouldShowAction,
    showAction,
  } = useShowActionModal(actionType.DEACTIVATE, 'employee', deactivateEmployee, activities);

  useEffect(() => {
    resetForm();
    setEnabled(employee.is_enabled);
    setPicture({ file: null, url: employee?.profilePictureURL ?? null });
    setValues({
      ...employee,
      birthdate: moment(employee.birthdate).format('YYYY-MM-DD'),
      city: employee?.city ?? '',
      country: employee?.country ?? 'France',
      gender: employee?.gender ?? '',
    });
  }, [employee, resetForm, setValues]);

  function deactivateEmployee() {
    onStatusChange({ is_enabled: false });
  }

  function handleCancelDeactivation() {
    setEnabled(true);
    resetShowAction();
  }

  const handleDeactivateOrDeleteAction = async (action, force = false) => {
    setActionLoading(true);
    const response = await onStatusChange({ action, is_enabled: false, force });
    setActionLoading(false);
    return response;
  };

  const handleTransferAction = async (transfer) => {
    await onStatusChange({ is_enabled: false, transfer });
  };

  const handleConfirmActionOnEmployeeModal = async ({ forceAction, selectedAction, transfer }) => {
    // Handle action selection submit
    if (selectedAction) {
      const { conflict } = await handleDeactivateOrDeleteAction(selectedAction);
      if (conflict) return setActionConflictSelect(selectedAction);
      return resetShowAction();
    }

    // Handle confirmation on delete or deactivation
    if (!isNil(forceAction)) {
      if (forceAction) {
        await handleDeactivateOrDeleteAction(previousChoice, true);
        return resetShowAction();
      }

      return setActionSelect();
    }

    // Handle transfer action
    if (transfer) {
      if (transfer === 'TRANSFER') {
        await handleTransferAction(true);
        return resetShowAction();
      }

      return setActionSelect();
    }

    // Handle preference passed
    setEnabled(false);
    if (activities.length > 0) return setActionTransfer();
    resetShowAction();
    onStatusChange({ is_enabled: false });
  };

  function handleEnabledChange(checked) {
    if (checked) {
      setEnabled(true);
      if (!isNil(employee.id)) onStatusChange({ is_enabled: true });
      return;
    }

    if (employee.is_new || isNil(employee.id)) {
      return setEnabled(false);
    }

    shouldShowAction();
  }

  const handlePictureChange = async (event) => {
    if (event.target.files != null && event.target.files.length > 0) {
      const file = new Blob([event.currentTarget.files[0]], { type: 'image/*' });
      const url = URL.createObjectURL(file);

      if (autoSavePicture) {
        setIsUpdatingPicture(true);
        const updated = await onPictureChange({ file, url });
        setPicture({ file: null, url: updated ? url : employee?.profilePictureURL ?? null });
        setIsUpdatingPicture(false);
      } else {
        setPicture({ file, url });
      }
    }
  };

  const handlePictureClick = (event) => {
    event.target.value = '';
  };

  const onCancelFormClick = () => {
    if (isSubmitting) return;

    onCancel();
    resetForm();
    setPicture({ file: null, url: employee?.profilePictureURL ?? null });
  };

  return (
    <form autoComplete="off" className={classes.root} onSubmit={handleSubmit}>
      <Grid container justifyContent="center" style={{ height: '100%' }}>
        <Grid
          container
          item
          xs={12}
          md={9}
          alignItems="center"
          justifyContent="center"
          direction="row"
          spacing={3}
          style={{ flexGrow: 1 }}
        >
          <Grid container item xs={12}>
            <Grid item xs={!dialog ? 3 : 4} className={classes.switchContainer}>
              <FormControlLabel
                className={classes.switch}
                labelPlacement="start"
                control={
                  <DefaultSwitch
                    checked={enabled}
                    checkedIcon={CheckIcon}
                    color={theme.palette.primary.main}
                    name="is_enabled"
                    onChange={(_, checked) => handleEnabledChange(checked)}
                  />
                }
                label="Statut"
              />
              <SwitchStatus status={enabled} />
            </Grid>
            <Grid item xs={3} className={classes.switchContainer} style={{ paddingLeft: useTheme().spacing(1) }}>
              <FormControlLabel
                className={classes.switch}
                labelPlacement="start"
                control={
                  <DefaultSwitch
                    checked={values.role === 'MONITEUR'}
                    checkedIcon={CheckIcon}
                    color={theme.palette.primary.main}
                    disabled
                    name="is_agenda_enabled"
                    onChange={handleChange}
                  />
                }
                label="Agenda"
              />
              <SwitchStatus status={values.role === 'MONITEUR'} />
            </Grid>
          </Grid>
          <Grid container item>
            <Grid container spacing={3}>
              <Grid item xs={3}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  label="Prénom"
                  margin="dense"
                  name="first_name"
                  variant="outlined"
                  value={values.first_name ?? ''}
                  onChange={handleChange}
                  error={touched.first_name && Boolean(errors.first_name)}
                  helperText={touched.first_name && errors.first_name}
                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  label="Nom"
                  margin="dense"
                  name="last_name"
                  variant="outlined"
                  value={values.last_name ?? ''}
                  onChange={handleChange}
                  error={touched.last_name && Boolean(errors.last_name)}
                  helperText={touched.last_name && errors.last_name}
                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  disabled
                  fullWidth
                  select
                  label="Rôle"
                  margin="dense"
                  name="role"
                  variant="outlined"
                  value={values.role}
                  onChange={handleChange}
                  error={touched.role && Boolean(errors.role)}
                  helperText={touched.role && errors.role}
                >
                  <DefaultMenuItem value="MONITEUR">Moniteur</DefaultMenuItem>
                </TextField>
              </Grid>

              <Grid item xs={3}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  select
                  label="Niveau"
                  margin="dense"
                  name="level"
                  variant="outlined"
                  value={values.level}
                  onChange={handleChange}
                  error={touched.level && Boolean(errors.level)}
                  helperText={touched.level && errors.level}
                >
                  <DefaultMenuItem value="beginner">Débutant</DefaultMenuItem>
                  <DefaultMenuItem value="intermediate">Intermédiaire</DefaultMenuItem>
                  <DefaultMenuItem value="expert">Expert</DefaultMenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Grid>

          <Grid container item>
            <Grid container spacing={3}>
              <Grid item xs={3}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  label="Date de naissance"
                  margin="dense"
                  name="birthdate"
                  variant="outlined"
                  type="date"
                  value={values.birthdate ?? ''}
                  onChange={handleChange}
                  error={touched.birthdate && Boolean(errors.birthdate)}
                  helperText={touched.birthdate && errors.birthdate}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  select
                  label="Genre"
                  margin="dense"
                  name="gender"
                  variant="outlined"
                  value={values.gender ?? ''}
                  onChange={handleChange}
                  error={touched.gender && Boolean(errors.gender)}
                  helperText={touched.gender && errors.gender}
                >
                  <DefaultMenuItem value="male">Homme</DefaultMenuItem>
                  <DefaultMenuItem value="female">Femme</DefaultMenuItem>
                </TextField>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  label="Adresse e-mail"
                  margin="dense"
                  name="email"
                  variant="outlined"
                  value={values.email ?? ''}
                  onChange={handleChange}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid container item>
            <Grid container spacing={3}>
              <Grid item xs={3}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  label="Ville"
                  margin="dense"
                  name="city"
                  variant="outlined"
                  value={values.city ?? ''}
                  onChange={handleChange}
                  error={touched.city && Boolean(errors.city)}
                  helperText={touched.city && errors.city}
                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  label="Pays"
                  margin="dense"
                  name="country"
                  variant="outlined"
                  value={values.country ?? 'France'}
                  onChange={handleChange}
                  error={touched.country && Boolean(errors.country)}
                  helperText={touched.country && errors.country}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  label="Téléphone"
                  margin="dense"
                  name="phone"
                  variant="outlined"
                  value={values.phone ?? ''}
                  onChange={handleChange}
                  error={touched.phone && Boolean(errors.phone)}
                  helperText={touched.phone && errors.phone}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid container item justifyContent="flex-start">
            <Box className={classes.imageContainer}>
              <img
                id="loadedProfilePictureImg"
                src={picture.url ?? placeholder}
                alt="profile"
                width="122px"
                height="122px"
                style={{ borderRadius: '5px' }}
              />

              <Box>
                <input
                  accept="image/*"
                  className={classes.input}
                  disabled={(!autoSavePicture && disabled) || isUpdatingPicture}
                  id={'profilePictureInput-' + id}
                  name="profilePicture"
                  type="file"
                  onChange={handlePictureChange}
                  onClick={handlePictureClick}
                />
                <label htmlFor={'profilePictureInput-' + id}>
                  <IconButton
                    aria-label="upload"
                    color="secondary"
                    component="span"
                    disabled={(!autoSavePicture && disabled) || isUpdatingPicture}
                  >
                    <ControlPointIcon style={{ height: '25px', width: '25px' }} fontSize="medium" />
                  </IconButton>
                </label>
                <Typography style={{ width: '80%', marginLeft: '10px' }}>
                  Vérifiez que l’image soit bien au format PNG ou JPG et qu’elle ne dépasse pas 5 Mo
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {!!dialog ? (
          <Grid container item xs={12} justifyContent="center" style={{ marginTop: '40px' }}>
            <DefaultButton cancel onClick={onCancelFormClick}>
              Annuler
            </DefaultButton>
            <DefaultButton disabled={isSubmitting} standard type="submit">
              {isSubmitting ? <CircularProgress size={20} /> : 'Valider'}
            </DefaultButton>
          </Grid>
        ) : (
          <Grid container item xs={12} justifyContent="center">
            <Grid container item xs={12} alignItems="flex-end" justifyContent="center" style={{ marginBottom: '16px' }}>
              {!disabled && (
                <>
                  <DefaultButton cancel onClick={onCancelFormClick}>
                    Annuler
                  </DefaultButton>
                  <DefaultButton color="secondary" disabled={isSubmitting} variant="contained" type="submit">
                    {isSubmitting ? <CircularProgress size={20} /> : 'Valider'}
                  </DefaultButton>
                </>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>

      <ActionOnEmployeeModal
        loading={actionLoading}
        modalType={actionType.DEACTIVATE}
        onClose={handleCancelDeactivation}
        onConfirm={handleConfirmActionOnEmployeeModal}
        showAction={showAction}
      />
    </form>
  );
}

export default EmployeeForm;
